// 12 sec Product Hero Shot's style picker (live's `styles-popover` over the shared
// `catalog-popover`, spec G14-templates): "Let the director decide", "Custom" and the card's
// styles (each poster, its clip on hover, its name, its product-fit hint), by live's tabs; the
// choice staged in "Selected" (Custom's own text there) until Apply.
import { Button, Image, Popover, Tabs, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import {
  Box,
  Brush,
  Camera,
  CassetteTape,
  Check,
  Film,
  LayoutGrid,
  Palette,
  PencilLine,
  Shapes,
  Smartphone,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";

import type { TemplateOptionView } from "../../../api/generated/model";
import { HoverVideo } from "../../../ui/HoverVideo";
import classes from "./forms.module.css";

/** Live's two styles that are no preset (`STYLE_MODES`), and its words (`constants.ts`). */
export const DIRECTOR = "director";
export const CUSTOM = "custom";
const DIRECTOR_LABEL = "Let the director decide";
const CUSTOM_LABEL = "Custom";
const ALL = "All";
const STYLE_TIP =
  "Choose the visual style for your hero shot, from cinematic and narrative looks to clean " +
  "product/studio shots, or let it be picked automatically. Choosing Custom style reveals a text " +
  "field to describe your own direction. Single-select.";
const CUSTOM_TITLE = "Describe your own look";
const CUSTOM_PLACEHOLDER = "Describe your own style direction…";
/** Ours (live sets none): the Custom style's text at most 2,000 characters. */
const MAX_CUSTOM = 2000;
/** Each tab's icon, live's (`style-presets.ts:126-215`): All, then its seven groups. */
const TAB_ICONS: Readonly<Record<string, LucideIcon>> = {
  [ALL]: LayoutGrid,
  Cinematic: Film,
  "Product & studio": Camera,
  Analog: CassetteTape,
  Animation: Shapes,
  "3D render": Box,
  Illustration: Brush,
  "Digital-native": Smartphone,
};

/** The style's name as the button shows it. */
export function styleLabel(style: string, options: readonly TemplateOptionView[]): string {
  if (style === DIRECTOR) return DIRECTOR_LABEL;
  if (style === CUSTOM) return CUSTOM_LABEL;
  return options.find((option) => option.id === style)?.label ?? DIRECTOR_LABEL;
}

export interface HeroStylePickerProps {
  style: string;
  customStyle: string;
  /** The card's styles, in live's order, each with its tab. */
  options: readonly TemplateOptionView[];
  disabled: boolean;
  onApply: (style: string, customStyle: string) => void;
}

/**
 * Live's picker: it opens on the chosen style's tab (All for a mode), lists the two modes first in
 * every tab, then the tab's styles; a click stages one; Apply writes it and closes.
 */
export function HeroStylePicker(props: HeroStylePickerProps) {
  const { style, customStyle, options, disabled, onApply } = props;
  const [opened, setOpened] = useState(false);
  const [staged, setStaged] = useState(style);
  const [text, setText] = useState(customStyle);
  const [tab, setTab] = useState(ALL);
  const tabs = [ALL, ...new Set(options.flatMap((option) => (option.group === null ? [] : [option.group])))];
  const open = () => {
    setStaged(style);
    setText(customStyle);
    setTab(options.find((option) => option.id === style)?.group ?? ALL);
    setOpened(true);
  };
  const shown = options.filter((option) => tab === ALL || option.group === tab);
  const chosen = options.find((option) => option.id === staged) ?? null;
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" width={792} shadow="md" trapFocus>
      <Popover.Target>
        <Tooltip label={STYLE_TIP} multiline maw={280} withArrow disabled={opened}>
          <UnstyledButton
            className={classes.option}
            style={{ width: 210 }}
            aria-label={`Style: ${styleLabel(style, options)}`}
            aria-expanded={opened}
            disabled={disabled}
            onClick={() => (opened ? setOpened(false) : open())}
          >
            <Palette size={16} strokeWidth={1.5} aria-hidden />
            <span>{styleLabel(style, options)}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown aria-label="Style" className={classes.hookPicker}>
        <div className={classes.hookCatalog}>
          <Text className={classes.hookTitle}>Style</Text>
          {/* Live's tabs (`ToggleChip`s): each its icon and name, the picked one on the slot's wash. */}
          <Tabs
            variant="none"
            value={tab}
            onChange={(next) => setTab(next ?? ALL)}
            classNames={{ list: classes.styleTabs, tab: classes.styleTab, tabSection: classes.styleTabIcon }}
          >
            <Tabs.List aria-label="Style groups">
              {tabs.map((each) => {
                const Icon = TAB_ICONS[each];
                return (
                  <Tabs.Tab
                    key={each}
                    value={each}
                    leftSection={Icon === undefined ? undefined : <Icon size={16} strokeWidth={1.5} aria-hidden />}
                  >
                    {each}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
          </Tabs>
          <div className={classes.hookGrid}>
            <ModeCard label={DIRECTOR_LABEL} icon={Sparkles} chosen={staged === DIRECTOR} onPick={() => setStaged(DIRECTOR)} />
            <ModeCard label={CUSTOM_LABEL} icon={PencilLine} chosen={staged === CUSTOM} onPick={() => setStaged(CUSTOM)} />
            {shown.map((option) => (
              <StyleTile key={option.id} option={option} chosen={staged === option.id} onPick={() => setStaged(option.id)} />
            ))}
          </div>
        </div>
        <div className={classes.hookSelected}>
          <Text className={classes.hookTitle}>Selected</Text>
          {/* Live's selected row, a card: the staged choice's picture (a style's poster, a mode's
              icon on lavender), its name, and Custom's own text under them. */}
          <ul className={classes.hookPicks} aria-label="Selected">
            <li className={classes.hookPick}>
              <span className={classes.hookPickRow}>
                {staged === DIRECTOR || staged === CUSTOM ? (
                  <span className={classes.hookPickIcon} aria-hidden>
                    {staged === DIRECTOR ? <Sparkles size={20} strokeWidth={1.5} /> : <PencilLine size={20} strokeWidth={1.5} />}
                  </span>
                ) : chosen?.poster_url ? (
                  <Image src={chosen.poster_url} alt="" className={classes.hookPickPoster} />
                ) : null}
                <Text className={classes.hookPickName}>{styleLabel(staged, options)}</Text>
              </span>
              {staged === CUSTOM ? (
                <Textarea
                  label={CUSTOM_TITLE}
                  aria-label={CUSTOM_TITLE}
                  placeholder={CUSTOM_PLACEHOLDER}
                  autosize
                  minRows={3}
                  maxRows={8}
                  maxLength={MAX_CUSTOM}
                  value={text}
                  onChange={(event) => setText(event.currentTarget.value)}
                  classNames={{ label: classes.customLabel }}
                />
              ) : null}
            </li>
          </ul>
          <Button
            fullWidth
            onClick={() => {
              onApply(staged, text);
              setOpened(false);
            }}
          >
            Apply
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/** A mode's card, live's lavender tile: its icon where a style has its poster. */
function ModeCard(props: { label: string; icon: LucideIcon; chosen: boolean; onPick: () => void }) {
  const Icon = props.icon;
  return (
    <UnstyledButton className={classes.hookCard} aria-label={props.label} aria-pressed={props.chosen} onClick={props.onPick}>
      <span className={`${classes.hookMedia} ${classes.styleMode}`}>
        <Icon size={32} strokeWidth={1.5} aria-hidden />
        {props.chosen ? <ChosenBadge /> : null}
      </span>
      <Text className={classes.hookName}>{props.label}</Text>
    </UnstyledButton>
  );
}

/** Live's check on the chosen card (`SelectionBadge`). */
function ChosenBadge() {
  return (
    <span className={classes.hookBadge} aria-hidden>
      <Check size={16} strokeWidth={2} />
    </span>
  );
}

/** A style's card (live's `CatalogCard`): its first-frame poster under its clip, which plays
 * silently while hovered (`HoverVideo`, as Hooks'), its name, its hint. */
function StyleTile(props: { option: TemplateOptionView; chosen: boolean; onPick: () => void }) {
  const { option, chosen, onPick } = props;
  const [hovered, setHovered] = useState(false);
  const card = (
    <UnstyledButton
      className={classes.hookCard}
      aria-label={option.label}
      aria-pressed={chosen}
      onClick={onPick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className={classes.hookMedia}>
        <HoverVideo src={option.clip_url} poster={option.poster_url} hovering={hovered} />
        {chosen ? <ChosenBadge /> : null}
      </span>
      <Text className={classes.hookName}>{option.label}</Text>
    </UnstyledButton>
  );
  return option.hint === null ? (
    card
  ) : (
    <Tooltip label={option.hint} position="bottom" maw={220} multiline openDelay={500} withArrow>
      {card}
    </Tooltip>
  );
}
